import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { api, AUTH_REQUIRED_EVENT } from "./lib/api";
import { AppShell } from "./components/AppShell";
import { PageLoading } from "./components/Loading";
import { AuthScreen } from "./pages/AuthScreen";
import { LibraryPage } from "./pages/LibraryPage";
import { DiscoverPage } from "./pages/DiscoverPage";
import { PlayerPage } from "./pages/PlayerPage";
import { EditorPage } from "./pages/EditorPage";
import { SettingsPage } from "./pages/SettingsPage";
import { NotFoundPage } from "./pages/NotFoundPage";

export default function App() {
  const queryClient = useQueryClient();
  const auth = useQuery({
    queryKey: ["auth"],
    queryFn: api.authStatus,
    retry: false
  });

  useEffect(() => {
    function handleAuthRequired() {
      queryClient.setQueryData(["auth"], {
        needsSetup: false,
        authenticated: false
      });
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    }

    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () =>
      window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, [queryClient]);

  if (auth.isLoading) {
    return (
      <div className="min-h-dvh bg-ink-950">
        <PageLoading label="正在确认学习室状态" />
      </div>
    );
  }

  if (auth.isError) {
    return (
      <div className="auth-canvas">
        <div className="auth-card">
          <span className="eyebrow">连接中断</span>
          <h1 className="font-display text-4xl text-paper">打不开学习室</h1>
          <p className="mt-3 text-paper-soft">
            电脑端服务可能没有启动。确认 API 进程和 Tailscale 连接后重新打开。
          </p>
        </div>
      </div>
    );
  }

  if (auth.data?.needsSetup || !auth.data?.authenticated) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <AuthScreen
              needsSetup={Boolean(auth.data?.needsSetup)}
              onAuthenticated={() =>
                queryClient.invalidateQueries({ queryKey: ["auth"] })
              }
            />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<LibraryPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="talks/:talkId" element={<PlayerPage />} />
        <Route path="talks/:talkId/edit" element={<EditorPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="library" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

